import { COOKIE_KEYS } from 'constants/common'
import {
  AuthContext,
  loginUserAction,
  logoutUserAction,
  setAnonymousAction,
} from 'context/AuthContext'
import { TWPERMISSIONS } from 'dataSource/auth/types'
import UserContext from 'context/common/User/UserContext'
import {
  JwtResponseDto,
  loginWithMagicLink,
  loginWithOTP,
  verifyWithOTP,
} from 'dataSource/auth'
import Cookies from 'js-cookie'
import { useContext, useEffect } from 'react'
import { getPermissionFromToken } from './helpers'
import { useRouter } from 'next/navigation'
import {
  EmailOTPResponse,
  MagicTokenResponse,
  RedirectLoginOptions,
  UseThriveAuthResult,
} from './types'
import { getSkipEmailAccessToken } from 'app/Bookings/utils'

const useThriveAuth = (): UseThriveAuthResult => {
  const router = useRouter()
  const [authState, dispatch] = useContext(AuthContext)
  const { clearUser, ...userContext } = useContext(UserContext)

  const loginWithRedirect = (options?: RedirectLoginOptions) => {
    if (options?.clearCredentials) {
      clearCredentials()
    }

    const query = new URLSearchParams(
      options?.appState as Record<string, string>
    )
    const queryString = query.toString() ? `?${query.toString()}` : ''
    router.replace(`/login${queryString}`)
  }

  const loginUser = (permissions: TWPERMISSIONS[]) =>
    dispatch(loginUserAction(permissions))

  const logoutUser = () => dispatch(logoutUserAction())

  const clearCredentials = () => {
    localStorage.removeItem('access_token')
    clearUser()
    Cookies.remove(COOKIE_KEYS.USER)
    Cookies.remove(COOKIE_KEYS.ACCESS_TOKEN)
    logoutUser()
  }

  const logout = async (options: { returnTo: string }) => {
    try {
      const { returnTo } = options
      clearCredentials()
      await router.replace(returnTo)
    } catch (error) {}
  }

  const setAnonymous = () => {
    dispatch(setAnonymousAction())
  }

  useEffect(() => {
    handleAuthentication({})
  }, [])

  const handleAuthentication = (
    props: JwtResponseDto
  ): TWPERMISSIONS[] | null => {
    // check jwt by getting user information
    const localToken = getAccessTokenSilently() ?? getSkipEmailAccessToken()
    const { access_token } = props
    const token = access_token || localToken

    if (token) {
      !getSkipEmailAccessToken() && localStorage.setItem('access_token', token)
      const permissions = getPermissionFromToken(token)
      // if the user is not created via skip email flow, then login the user
      !getSkipEmailAccessToken() && loginUser(permissions)
      return permissions
    } else {
      setAnonymous()
    }

    return null
  }

  const requestMagicLink = async (
    email: string,
    params: {
      redirectTo: string
    }
  ): Promise<MagicTokenResponse> => {
    const result = (await loginWithMagicLink(
      email,
      params
    )) as MagicTokenResponse
    return result
  }

  const requestOTP = async (
    email: string,
    isRetry?: boolean
  ): Promise<EmailOTPResponse> => {
    const result = (await loginWithOTP(email, isRetry)) as EmailOTPResponse
    return result
  }

  const verifyOTP = async (
    email: string,
    otp: string
  ): Promise<JwtResponseDto> => {
    const authResponse = await verifyWithOTP(email, otp)
    return authResponse
  }

  const getAccessTokenSilently = () => {
    return localStorage.getItem('access_token')
  }

  const isSupportUser = authState.permissions.includes(TWPERMISSIONS.SUPPORT)
  const isSoftwareDevs = authState.permissions.includes(
    TWPERMISSIONS.SOFTWARE_DEVS
  )
  const isClinician = authState.permissions.includes(TWPERMISSIONS.CLINICIAN)
  const isDevOpsAdmin = authState.permissions.includes(
    TWPERMISSIONS.DEVOPS_ADMIN
  )
  const isClinicalAdmin = authState.permissions.includes(
    TWPERMISSIONS.CLINICAL_ADMIN
  )
  const isThirdPartyAgent = authState.permissions.includes(
    TWPERMISSIONS.THIRD_PARTY_SUPPORT
  )
  const isSupportOrThirdPartyUser = isSupportUser || isThirdPartyAgent
  const isPatient =
    authState.permissions.includes(TWPERMISSIONS.PATIENT) &&
    !isSupportOrThirdPartyUser

  return {
    ...authState,
    ...userContext,
    clearCredentials,
    clearUser,

    getAccessTokenSilently,
    isPatient,
    isSupportUser,
    isClinician,
    isDevOpsAdmin,
    isClinicalAdmin,
    isSoftwareDevs,
    isThirdPartyAgent,
    isSupportOrThirdPartyUser,
    loginWithRedirect,
    logout,
    requestMagicLink,
    requestOTP,
    verifyOTP,
    handleAuthentication,
  }
}

export default useThriveAuth
